import React, { useEffect } from 'react';
import { Form as FormikForm, useFormikContext } from 'formik';

import { UserMe } from 'legacy/user/types';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { FormValueDto } from '../helpers/computeInitialValues';

import { GoToStepFn } from '../types';
import { OutgoingEventProductDto } from 'api/dtos/events/products';
import useManageProducts from 'legacy/events/hooks/useManageProducts';
import { useEventEditorContext } from 'legacy/events/contexts/EventEditorContext';
import ProductTypeSelector from './ProductTypeSelector';
import ProductTypeFactory from './ProductTypeFactory';
import AdditionalOptions from './AdditionalOptions';

type MainFormProps = {
  isTierPrivate: boolean;
  ticket?: OutgoingEventProductDto;
  user: UserMe;
  isEditing: boolean;
  goToStep: GoToStepFn;
  setIsDirty: (dirty: boolean) => void;
  handleSubmit: () => void;
  handleRemoveClick: () => void;
  isRemoveLoading: boolean;
  isSubmitLoading: boolean;
};

const modalTexts = {
  publicTier: {
    cta: 'Save public tier'
  },
  privateTier: {
    cta: 'Save private tier'
  }
};

const MainForm: React.FC<MainFormProps> = ({
  isTierPrivate,
  ticket,
  user,
  isEditing,
  goToStep,
  setIsDirty,
  handleSubmit,
  handleRemoveClick,
  isRemoveLoading,
  isSubmitLoading
}) => {
  const texts = modalTexts[isTierPrivate ? 'privateTier' : 'publicTier'];
  const { values, dirty, setFieldValue } = useFormikContext<FormValueDto>();
  const { eventId } = useEventEditorContext();
  const { products } = useManageProducts(eventId);
  const isEditingTicket = !!ticket;

  // Get only standard products (admission or addon) that can be used in bundles
  const standardProducts = products.filter(
    (p) =>
      (p.type === 'admission' || p.type === 'addon') &&
      p.isActive &&
      p.id !== ticket?.id
  );

  const isUnsaved = (field: string) =>
    dirty && values[field] !== ticket?.[field];

  useEffect(() => {
    setIsDirty(dirty);
  }, [dirty]);

  useEffect(() => {
    if (values.type === 'bundle') {
      if (!values.components || values.components.length === 0) {
        setFieldValue('components', [{ productId: '', quantity: 1 }]);
      }
    }
  }, [values.type, setFieldValue]);

  return (
    <>
      <div className="pb-24 md:pb-16">
        <div className="p-4">
          <FormikForm>
            <ProductTypeSelector
              disabled={isEditingTicket}
              isPrivate={isTierPrivate}
            />

            {/* Render type-specific fields (including common fields) using the factory */}
            <ProductTypeFactory
              standardProducts={standardProducts}
              user={user}
              isUnsaved={isUnsaved}
            />

            <AdditionalOptions
              isEditing={isEditingTicket}
              ticket={ticket}
              goToStep={goToStep}
              isUnsaved={isUnsaved}
            />
          </FormikForm>
        </div>
      </div>
      <div className="w-full fixed left-0 bottom-0 bg-white pb-8 z-50 md:pb-4 border-t-1 px-4 py-4 shadow-primary-50 shadow-top md:shadow-none">
        <div className="flex gap-3 justify-center">
          {isEditing && (
            <Button
              type={ButtonType.Danger}
              className={`w-full md:max-w-[343px] ${
                ticket && !ticket.canDelete
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
              }`}
              onClick={handleRemoveClick}
              loading={isRemoveLoading}
            >
              Remove
            </Button>
          )}
          <Button
            className="w-full md:max-w-[343px] bg-[#3853C4]! text-white"
            type={ButtonType.Primary}
            onClick={handleSubmit}
            loading={isSubmitLoading}
          >
            {texts.cta}
          </Button>
        </div>
      </div>
    </>
  );
};

export default MainForm;
